import { Button, Card, Form, Space, Table, Tag } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import { EducationSessionSelect } from '@/pages/education/components/EducationLookupSelects';

type Period = {
  id: string;
  code: string;
  name: string;
  sequence_no: number;
  start_time?: string | null;
  end_time?: string | null;
  is_break?: number | boolean;
  session_name?: string;
};

export default function TimetablePeriodsPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.timetable.manage') || user?.permissions?.includes('education.admin')
  );
  const [sessionId, setSessionId] = useState<string | undefined>();

  const list = useQuery({
    queryKey: ['education-timetable-periods', sessionId],
    enabled: Boolean(sessionId),
    queryFn: async () => {
      const res = await api.get<Period[]>('/education/timetable/periods', {
        params: { session_id: sessionId },
      });
      return Array.isArray(res.data) ? res.data : [];
    },
  });

  return (
    <EducationPageChrome
      title={t('timetablePeriods')}
      description={t('timetablePeriodsIntro')}
      extra={
        <Space>
          {canManage && sessionId ? (
            <Button
              type="primary"
              onClick={() => navigate(`/education/timetable/periods/new?session_id=${sessionId}`)}
            >
              {t('newPeriod')}
            </Button>
          ) : null}
          <Button onClick={() => navigate('/education/timetable')}>{t('backToTimetable')}</Button>
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card size="small">
          <Form layout="inline">
            <Form.Item label={t('session')} required>
              <EducationSessionSelect
                style={{ minWidth: 220 }}
                value={sessionId}
                onChange={(v) => setSessionId(v || undefined)}
              />
            </Form.Item>
          </Form>
        </Card>
        <Card size="small">
          {!sessionId ? (
            t('timetablePickSession')
          ) : (
            <Table
              rowKey="id"
              size="small"
              loading={list.isLoading}
              dataSource={list.data ?? []}
              pagination={false}
              columns={[
                { title: t('sequence'), dataIndex: 'sequence_no', width: 90 },
                { title: t('code'), dataIndex: 'code', width: 100 },
                { title: t('name'), dataIndex: 'name' },
                {
                  title: t('time'),
                  width: 160,
                  render: (_: unknown, row: Period) =>
                    row.start_time || row.end_time
                      ? `${row.start_time || '—'} – ${row.end_time || '—'}`
                      : '—',
                },
                {
                  title: t('break'),
                  width: 90,
                  render: (_: unknown, row: Period) =>
                    row.is_break === 1 || row.is_break === true ? <Tag>{t('break')}</Tag> : '—',
                },
                ...(canManage
                  ? [
                      {
                        title: t('actions'),
                        width: 100,
                        render: (_: unknown, row: Period) => (
                          <Button size="small" onClick={() => navigate(`/education/timetable/periods/${row.id}/edit`)}>
                            {t('edit')}
                          </Button>
                        ),
                      },
                    ]
                  : []),
              ]}
            />
          )}
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
